import { withNamespaces } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useContext } from "react";
import { Col, Container, Row } from "reactstrap";
import QueryContext from "../../../../../contexts/QueryContext";

import ComplianceScreeningQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueriesService";
import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";

import { createColorFromString } from "src/modules/3rd-party-management/helpers/common";

const CountriesChart = ({ t, countries }) => {
    const countriesColors = [];
    const countriesLabels = [];
    const countriesSeries = [];

    for(const countryName in countries) {
        if (countryName === 'not_defined') {
            countriesLabels.push(t('Unspecified'));
            countriesColors.push('#9CA3AF');
            countriesSeries.push(countries[countryName]);
        }
        else {
            countriesLabels.push(t(countryName));
            countriesColors.push(createColorFromString(countryName));
            countriesSeries.push(countries[countryName]);
        }
    }

    const isSeriesEmpty = countriesSeries.every((s) => s === 0)

    const countriesChartOptions = {
        plotOptions: {
            pie: {
                donut: {
                    size: "75%"
                }
            }
        },
        dataLabels: {
            enabled: true
        },
        legend: {
            show: true,
            position: 'right'
        },
        colors: isSeriesEmpty ? ["e0e0e0"] : countriesColors,
        labels: isSeriesEmpty ? [t("No Data")] : countriesLabels
    };

    return (
        <Col xs="12" sm="4" className="d-flex align-items-center flex-column justify-content-center">
            <p className="fw-bold">
                { t('Countries') }
            </p>
            <ReactApexChart
                options={countriesChartOptions}
                series={isSeriesEmpty ? [1] : countriesSeries}
                type="donut"
                height="120"
            />
        </Col>
    )
}

const TypeChart = ({ t, companyItemsCount, individualItemsCount }) => {
    const typeChartOptions = {
        plotOptions: {
          pie: {
            donut: {
                size: "75%"
            }
          }
        },
        dataLabels: {
          enabled: true
        },
        legend: {
          show: true,
          position: 'right'
        },
        colors: [
            '#1ABC8C',
            '#8792E8'
        ],
        labels: [
            t('Companies'),
            t('Individuals')
        ]
    };

    return (
        <Col xs="12" sm="4" className="d-flex align-items-center flex-column justify-content-center">
            <p className="fw-bold">
                { t('Type') }
            </p>
            <ReactApexChart
                options={typeChartOptions}
                series={[
                    companyItemsCount || 0,
                    individualItemsCount || 0
                ]}
                type="donut"
                height="120"
            />
        </Col>
    )
}

const OverviewHeader = ({
    t
}) => {
    const {
        queryType,
        queryData
    } = useContext(QueryContext);

    const service = queryType === 'screening' ? 
        ComplianceScreeningQueriesService.getInstance() : 
        ComplianceMonitoringQueriesService.getInstance();

    const overviewQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-query-items-overview',
            queryData.id
        ],
        queryFn: async () => {
            return await service.fetchQueryItemsOverview(queryData.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Unable to load overview data. Please try again.'), {
                type: 'error'
            });
        }
    });

    const renderLoadingSection = () => {
        return (
            <Row>
                <Col sm="12">
                    <div className="loading-section p-5">
                        <Row className="mb-5">
                            <Col sm="12" md="4">
                                <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                                <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                            </Col>
                            <Col sm="12" md="4">
                                <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                                <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                            </Col>
                            <Col sm="12" md="4">
                                <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                                <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        )
    }

    const renderContent = () => {
        const {
            header,
            country = {}
        } = overviewQuery.data || {};

        return (
            <div className="report-details-box" style={{ minHeight: '220px' }}>
                <Container>
                    <Row>
                        <Col xs="12" sm="4" className="d-flex align-items-center flex-column justify-content-center">
                            <p className="fw-bold">
                                { t('Search Items') }
                            </p>
                            <p>
                                { header?.allItemsCount || 0 }
                            </p>
                        </Col>

                        <TypeChart 
                            t={t}
                            {...header}
                        />
                    
                        <CountriesChart 
                            t={t}
                            countries={country}
                        />
                    </Row>
                </Container>
            </div>
        )
    }

    if (overviewQuery.isFetching || overviewQuery.isError) {
        return renderLoadingSection()
    }

    return renderContent();
};

export default withNamespaces()(OverviewHeader);